import React from 'react'
import { useNavigate } from 'react-router-dom'
import { AppLayout } from '../components/layout/AppLayout'
import { PageContainer } from '../components/layout/PageContainer'
import { Box, Text, Button, Card } from '../components/ui/foundation'
import { Badge } from '../components/ui/foundation/Badge'
import { secondaryCardClass } from '../components/secondary-pages/styles'
import {
  useOrders,
  formatOrderPrice,
  formatOrderDate,
} from '../hooks/useOrders'
import type { Database } from '../types/database'
import {
  ArrowSquareOut as ExternalLink,
  Package,
  CaretRight as ChevronRight,
} from '@phosphor-icons/react'
import { MetaTags } from '../components/seo/MetaTags'

type Order = Database['public']['Tables']['orders']['Row']
type OrderWithProduct = Order & {
  products: Database['public']['Tables']['products']['Row'] | null
}

const skeletonStaggerDelays = ['0ms', '60ms', '120ms'] as const

const SkeletonBlock: React.FC<{
  className: string
  delay?: (typeof skeletonStaggerDelays)[number]
}> = ({ className, delay = '0ms' }) => (
  <div
    aria-hidden="true"
    className={`rounded-lg bg-[linear-gradient(90deg,rgba(236,227,200,0.9)_25%,rgba(248,244,235,0.95)_50%,rgba(236,227,200,0.9)_75%)] bg-[length:200%_100%] animate-[shimmer_1.5s_ease-in-out_infinite] ${className}`}
    style={{ animationDelay: delay }}
  />
)

// Status mapping for clean badge display
const getStatusInfo = (
  orderStatus: string | null,
  fulfillmentStatus: string | null
): {
  label: string
  variant:
    | 'delivered'
    | 'shipped'
    | 'processing'
    | 'pending'
    | 'unfulfilled'
    | 'cancelled'
    | 'error'
    | 'default'
} => {
  const status = fulfillmentStatus || orderStatus
  if (!status) return { label: 'Pending', variant: 'pending' }

  const statusLower = status.toLowerCase()
  if (statusLower === 'delivered')
    return { label: 'Delivered', variant: 'delivered' }
  if (statusLower === 'shipped') return { label: 'Shipped', variant: 'shipped' }
  if (statusLower === 'fulfilled')
    return { label: 'Fulfilled', variant: 'delivered' }
  if (statusLower === 'waiting_for_print_asset') {
    return { label: 'Preparing Print File', variant: 'processing' }
  }
  if (statusLower === 'paid' || statusLower === 'processing')
    return { label: 'Processing', variant: 'processing' }
  if (statusLower === 'pending') return { label: 'Pending', variant: 'pending' }
  if (statusLower === 'unfulfilled')
    return { label: 'Unfulfilled', variant: 'unfulfilled' }
  if (statusLower === 'cancelled')
    return { label: 'Cancelled', variant: 'cancelled' }
  if (statusLower === 'refunded') return { label: 'Refunded', variant: 'error' }
  return { label: status, variant: 'default' }
}

// Table row component for desktop
const OrderRow: React.FC<{ order: OrderWithProduct }> = ({ order }) => {
  const status = getStatusInfo(order.order_status, order.fulfillment_status)

  return (
    <tr className="group border-b border-[rgba(36,34,30,0.06)] last:border-b-0 transition-colors duration-150 hover:bg-[rgba(36,34,30,0.025)]">
      <td className="py-4 px-6">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg bg-background-100">
            {order.products?.thumbnail_url ? (
              <img
                src={order.products.thumbnail_url}
                alt={order.products?.title || 'Vision Board Print'}
                className="h-full w-full object-cover"
              />
            ) : (
              <Package className="h-5 w-5 text-ink-400" />
            )}
          </div>
          <span className="font-sans text-fs-400 font-normal leading-relaxed text-ink-500">
            {order.products?.title || 'Vision Board Print'}
          </span>
        </div>
      </td>

      {/* Status */}
      <td className="py-4 px-6">
        <Badge status={status.variant} size="sm">
          {status.label}
        </Badge>
      </td>

      {/* Date */}
      <td className="py-4 px-6 font-sans text-fs-400 font-normal text-ink-600">
        {formatOrderDate(order.created_at)}
      </td>

      {/* Size */}
      <td className="py-4 px-6 font-sans text-fs-400 font-normal text-ink-600 capitalize">
        {order.print_size}
      </td>

      {/* Price */}
      <td className="py-4 px-6 text-right font-sans text-fs-400 font-semibold text-ink-700">
        {formatOrderPrice(order.total_amount)}
      </td>

      {/* Action */}
      <td className="py-4 px-6 text-right">
        {order.order_status_url ? (
          <a
            href={order.order_status_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 font-sans text-fs-400 font-medium text-primary-300 transition-colors hover:text-primary-400"
          >
            View
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        ) : order.tracking_url ? (
          <a
            href={order.tracking_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 font-sans text-fs-400 font-medium text-primary-300 transition-colors hover:text-primary-400"
          >
            Track
            <ExternalLink className="w-3.5 h-3.5" />
          </a>
        ) : (
          <span className="font-sans text-fs-400 font-normal text-ink-300">
            —
          </span>
        )}
      </td>
    </tr>
  )
}

// Mobile card for responsive design
const MobileOrderCard: React.FC<{ order: OrderWithProduct }> = ({ order }) => {
  const status = getStatusInfo(order.order_status, order.fulfillment_status)

  return (
    <div className="border-b border-[rgba(36,34,30,0.06)] p-4 last:border-b-0">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg bg-background-100">
            {order.products?.thumbnail_url ? (
              <img
                src={order.products.thumbnail_url}
                alt={order.products?.title || 'Vision Board Print'}
                className="h-full w-full object-cover"
              />
            ) : (
              <Package className="h-5 w-5 text-ink-400" />
            )}
          </div>
          <p className="font-sans text-fs-400 font-normal leading-relaxed text-ink-500">
            {order.products?.title || 'Vision Board Print'}
          </p>
        </div>
        <Badge status={status.variant} size="sm">
          {status.label}
        </Badge>
      </div>

      <div className="flex items-center justify-between font-sans text-fs-400">
        <div className="flex items-center gap-4 font-normal text-ink-500">
          <span>{formatOrderDate(order.created_at)}</span>
          <span className="capitalize">{order.print_size}</span>
        </div>
        <span className="font-semibold text-ink-700">
          {formatOrderPrice(order.total_amount)}
        </span>
      </div>

      {(order.order_status_url || order.tracking_url) && (
        <div className="mt-3 border-t border-[rgba(36,34,30,0.06)] pt-3">
          <a
            href={order.order_status_url || order.tracking_url || '#'}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 font-sans text-fs-400 font-medium text-primary-300 transition-colors hover:text-primary-400"
          >
            {order.order_status_url ? 'View Order' : 'Track Shipment'}
            <ChevronRight className="w-4 h-4" />
          </a>
        </div>
      )}
    </div>
  )
}

const OrdersTableSkeleton: React.FC = () => (
  <Card
    aria-hidden="true"
    className={`${secondaryCardClass} overflow-hidden opacity-100 transition-opacity duration-settle ease-precise`}
  >
    <div className="hidden md:block">
      <table className="w-full">
        <thead>
          <tr className="border-b border-[rgba(36,34,30,0.06)] bg-white">
            <th className="py-3 px-6 text-left font-sans text-fs-200 font-medium uppercase tracking-kicker text-ink-400">
              Order
            </th>
            <th className="py-3 px-6 text-left font-sans text-fs-200 font-medium uppercase tracking-kicker text-ink-400">
              Status
            </th>
            <th className="py-3 px-6 text-left font-sans text-fs-200 font-medium uppercase tracking-kicker text-ink-400">
              Date
            </th>
            <th className="py-3 px-6 text-left font-sans text-fs-200 font-medium uppercase tracking-kicker text-ink-400">
              Size
            </th>
            <th className="py-3 px-6 text-right font-sans text-fs-200 font-medium uppercase tracking-kicker text-ink-400">
              Total
            </th>
            <th className="py-3 px-6 text-right font-sans text-fs-200 font-medium uppercase tracking-kicker text-ink-400">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {skeletonStaggerDelays.map((delay, index) => (
            <tr
              key={delay}
              aria-hidden="true"
              className="border-b border-[rgba(36,34,30,0.06)] last:border-b-0"
            >
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <SkeletonBlock
                    className="h-10 w-10 rounded-[12px]"
                    delay={delay}
                  />
                  <div className="flex flex-col gap-2">
                    <SkeletonBlock className="h-3 w-24" delay={delay} />
                    <SkeletonBlock
                      className={`h-3 ${index === 1 ? 'w-36' : 'w-32'}`}
                      delay={delay}
                    />
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <SkeletonBlock
                  className="h-6 w-20 rounded-full"
                  delay={delay}
                />
              </td>
              <td className="px-6 py-4">
                <SkeletonBlock className="ml-0 h-3 w-20" delay={delay} />
              </td>
              <td className="px-6 py-4">
                <SkeletonBlock className="h-3 w-14" delay={delay} />
              </td>
              <td className="px-6 py-4">
                <div className="flex justify-end">
                  <SkeletonBlock className="h-3 w-16" delay={delay} />
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="flex justify-end">
                  <SkeletonBlock className="h-3 w-12" delay={delay} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <div className="md:hidden">
      {skeletonStaggerDelays.map((delay, index) => (
        <div
          key={delay}
          aria-hidden="true"
          className="border-b border-[rgba(36,34,30,0.06)] p-4 last:border-b-0"
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <SkeletonBlock
                className="h-10 w-10 rounded-[12px]"
                delay={delay}
              />
              <div className="space-y-2">
                <SkeletonBlock className="h-3 w-24" delay={delay} />
                <SkeletonBlock
                  className={`h-3 ${index === 2 ? 'w-28' : 'w-32'}`}
                  delay={delay}
                />
              </div>
            </div>
            <SkeletonBlock className="h-6 w-20 rounded-full" delay={delay} />
          </div>

          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <SkeletonBlock className="h-3 w-16" delay={delay} />
              <SkeletonBlock className="h-3 w-12" delay={delay} />
            </div>
            <SkeletonBlock className="h-3 w-16" delay={delay} />
          </div>

          <div className="mt-4 border-t border-[rgba(36,34,30,0.06)] pt-3">
            <SkeletonBlock className="h-3 w-24" delay={delay} />
          </div>
        </div>
      ))}
    </div>
  </Card>
)

export const OrdersPage: React.FC = () => {
  const navigate = useNavigate()
  const { orders, initialLoading, error } = useOrders()

  return (
    <AppLayout disableContentEntrance>
      <MetaTags
        title="Prints | Printboard"
        description="Track your Printboard orders and printed pieces."
        noindex={true}
      />
      <PageContainer
        maxWidth="full"
        className="px-4 pb-12 pt-8 md:px-6 lg:px-8 xl:px-12"
      >
        <h1 className="sr-only">Prints</h1>

        <Box>
          <div className="mx-auto max-w-[1200px]">
            <p className="mb-4 font-sans text-fs-300 text-ink-400">Prints</p>

            {error ? (
              <Card className={`${secondaryCardClass} p-8 text-center`}>
                <Text className="text-sm font-normal leading-relaxed text-ink-600 mb-4">
                  {error}
                </Text>
                <Button
                  variant="secondary"
                  onClick={() => window.location.reload()}
                >
                  Try Again
                </Button>
              </Card>
            ) : initialLoading ? (
              <OrdersTableSkeleton />
            ) : orders.length === 0 ? (
              <Card
                className={`${secondaryCardClass} px-6 py-10 text-center sm:px-10`}
              >
                <div className="mx-auto flex max-w-sm flex-col items-center">
                  <Package className="mb-4 h-8 w-8 text-ink-400" />
                  <h2 className="font-sans text-[20px] font-semibold leading-[1.3] text-ink-700">
                    Your prints will appear here.
                  </h2>
                  <Button
                    variant="secondary"
                    onClick={() => navigate('/boards')}
                    className="mt-6"
                  >
                    Create a Board
                  </Button>
                </div>
              </Card>
            ) : (
              <Card className={`${secondaryCardClass} overflow-hidden`}>
                <div className="hidden md:block">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[rgba(36,34,30,0.06)] bg-white">
                        <th className="py-3 px-6 text-left font-sans text-fs-200 font-medium uppercase tracking-kicker text-ink-400">
                          Order
                        </th>
                        <th className="py-3 px-6 text-left font-sans text-fs-200 font-medium uppercase tracking-kicker text-ink-400">
                          Status
                        </th>
                        <th className="py-3 px-6 text-left font-sans text-fs-200 font-medium uppercase tracking-kicker text-ink-400">
                          Date
                        </th>
                        <th className="py-3 px-6 text-left font-sans text-fs-200 font-medium uppercase tracking-kicker text-ink-400">
                          Size
                        </th>
                        <th className="py-3 px-6 text-right font-sans text-fs-200 font-medium uppercase tracking-kicker text-ink-400">
                          Total
                        </th>
                        <th className="py-3 px-6 text-right font-sans text-fs-200 font-medium uppercase tracking-kicker text-ink-400">
                          <span className="sr-only">Actions</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((order) => (
                        <OrderRow key={order.id} order={order} />
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="md:hidden">
                  {orders.map((order) => (
                    <MobileOrderCard key={order.id} order={order} />
                  ))}
                </div>
              </Card>
            )}
          </div>
        </Box>
      </PageContainer>
    </AppLayout>
  )
}

export default OrdersPage
